/**
 * Admin navigation and in-page guides — ordered like a paper school office.
 * Plain language for staff who are used to registers, ledgers, and notice boards.
 */

import { getHomeroomTeacherTerm } from "../utils/homeroomTeacherTerm";

function applyHomeroomTerms(text, term) {
    if (!text || typeof text !== "string" || !term?.primary) return text;
    return text
        .replace(/form teachers/gi, term.plural)
        .replace(/form-teacher/gi, `${term.enrollHint}`)
        .replace(/form teacher/gi, term.assignOnClass);
}

function mapGuideCopy(guide, term) {
    return {
        ...guide,
        title: applyHomeroomTerms(guide.title, term),
        steps: Array.isArray(guide.steps) ? guide.steps.map((s) => applyHomeroomTerms(s, term)) : guide.steps,
        tip: applyHomeroomTerms(guide.tip, term),
    };
}

export function getAdminNavSections(schoolType) {
    const term = getHomeroomTeacherTerm(schoolType);
    return ADMIN_NAV_SECTIONS.map((section) => ({
        ...section,
        summary: applyHomeroomTerms(section.summary, term),
        items: section.items.map((item) => ({
            ...item,
            label: applyHomeroomTerms(item.label, term),
            hint: applyHomeroomTerms(item.hint, term),
        })),
    }));
}

export function getAdminPageGuides(schoolType) {
    const term = getHomeroomTeacherTerm(schoolType);
    return ADMIN_PAGE_GUIDES.map((g) => mapGuideCopy(g, term));
}

/** Sidebar sections in the order a new school should follow. */
export const ADMIN_NAV_SECTIONS = [
    {
        id: "start",
        stepLabel: "Home",
        title: "Start here",
        summary: "Your daily overview",
        defaultOpen: true,
        items: [
            {
                to: "/Admin/dashboard",
                label: "Office dashboard",
                hint: "Like the head teacher’s daily diary",
                match: (p) => p === "/Admin/dashboard" || p === "/Admin",
            },
            {
                to: "/Admin/analytics",
                label: "Advanced analytics",
                hint: "Trends, at-risk students, library insights, exports",
                match: (p) => p.startsWith("/Admin/analytics"),
            },
            {
                to: "/Admin/emis-reports",
                label: "EMIS / DEMIS reports",
                hint: "Ministry census & exports for district returns",
                match: (p) => p.startsWith("/Admin/emis-reports"),
            },
        ],
    },
    {
        id: "setup",
        stepLabel: "Step 1",
        title: "Set up the school",
        summary: "Classes, subjects & staff — do this first",
        items: [
            {
                to: "/Admin/addclass",
                label: "Add a class",
                hint: "New class register + assign form teacher",
                match: (p) => p.startsWith("/Admin/addclass"),
            },
            {
                to: "/Admin/classes",
                label: "All classes",
                hint: "Set form teacher per class",
                match: (p) => p.startsWith("/Admin/classes") && !p.includes("/results"),
            },
            {
                to: "/Admin/subjects/changes",
                label: "Subject change approvals",
                hint: "Mid-term teacher subject changes",
                match: (p) => p.startsWith("/Admin/subjects/changes"),
            },
            {
                to: "/Admin/subjects/chooseclass",
                label: "Subjects",
                hint: "Pick a class, then view subjects",
                match: (p) => p.startsWith("/Admin/subjects") || p.startsWith("/Admin/subject") || p.startsWith("/Admin/addsubject"),
            },
            {
                to: "/Admin/teachers",
                label: "Staff",
                hint: "Teachers, bursar, HOD, librarian",
                match: (p) =>
                    (p.startsWith("/Admin/teachers") || p.startsWith("/Admin/staff")) &&
                    !p.startsWith("/Admin/teachers/management") &&
                    !p.startsWith("/Admin/staff/attendance"),
            },
            {
                to: "/Admin/teachers/management",
                label: "Teacher HR & TSC",
                hint: "Qualifications, CPD, payroll, deployment",
                match: (p) => p.startsWith("/Admin/teachers/management"),
            },
            {
                to: "/Admin/school-page",
                label: "Parents’ school page",
                hint: "Public page for guardians",
                match: (p) => p.startsWith("/Admin/school-page"),
            },
        ],
    },
    {
        id: "students",
        stepLabel: "Step 2",
        title: "Students",
        summary: "Enrol pupils & class lists",
        items: [
            {
                to: "/Admin/students/bulk-upload",
                label: "Bulk upload (CSV/Excel)",
                hint: "Pick a class, then import CSV or Excel",
                match: (p) => p.includes("/bulk-upload"),
            },
            {
                to: "/Admin/students/chooseclass",
                label: "Add a student (director)",
                hint: "Instant verified enrolment — or use form teacher flow",
                match: (p) => p.startsWith("/Admin/students/chooseclass") || p.startsWith("/Admin/addstudents"),
            },
            {
                to: "/Admin/students",
                label: "All students",
                hint: "Master student list",
                match: (p) =>
                    p.startsWith("/Admin/students") &&
                    !p.includes("/chooseclass") &&
                    !p.includes("/report-card") &&
                    !p.includes("/transcript"),
            },
            {
                to: "/Admin/enrollment/review",
                label: "Verify enrollments",
                hint: "Form-teacher pupils & anti–fee theft roll check",
                match: (p) => p.startsWith("/Admin/enrollment/review"),
            },
            {
                to: "/Admin/students/rollover",
                label: "Term & year rollover",
                hint: "Mark leavers; promote classes; graduate Std 8 & Form 4",
                match: (p) => p.startsWith("/Admin/students/rollover"),
            },
            {
                to: "/Admin/complains",
                label: "Complaints",
                hint: "Complaints book",
                match: (p) => p.startsWith("/Admin/complains"),
            },
        ],
    },
    {
        id: "daily",
        stepLabel: "Step 3",
        title: "Every school day",
        summary: "Notices, attendance & marks",
        items: [
            {
                to: "/Admin/addnotice",
                label: "Post a notice",
                hint: "Notice board",
                match: (p) => p.startsWith("/Admin/addnotice"),
            },
            {
                to: "/Admin/notices",
                label: "All notices",
                hint: "Past announcements",
                match: (p) => p.startsWith("/Admin/notices"),
            },
            {
                to: "/Admin/attendance/review",
                label: "Check attendance",
                hint: "Review teachers’ attendance books",
                match: (p) => p.startsWith("/Admin/attendance/review"),
            },
            {
                to: "/Admin/staff/attendance",
                label: "Staff sign-in book",
                hint: "Record arrival & departure for all staff",
                match: (p) => p.startsWith("/Admin/staff/attendance"),
            },
            {
                to: "/Admin/exams/review",
                label: "Approve class results",
                hint: "Form teacher packages → publish to students",
                match: (p) =>
                    p.startsWith("/Admin/exams/review") ||
                    p.startsWith("/Admin/submissions"),
            },
            {
                to: "/Admin/schemes",
                label: "Scheme of work",
                hint: "Approve teachers’ plans",
                match: (p) => p.startsWith("/Admin/schemes"),
            },
            {
                to: "/Admin/leaves",
                label: "Teacher leave",
                hint: "Leave application file",
                match: (p) => p.startsWith("/Admin/leaves"),
            },
            {
                to: "/Admin/disciplinary",
                label: "Disciplinary cases",
                hint: "Teacher reports → verify outcomes",
                match: (p) => p.startsWith("/Admin/disciplinary"),
            },
        ],
    },
    {
        id: "exams",
        stepLabel: "Step 4",
        title: "Exams & results",
        summary: "Marks, report cards & transcripts",
        items: [
            {
                to: "/Admin/exams/records",
                label: "Results records",
                hint: "Published results by class — official archive",
                match: (p) =>
                    p.startsWith("/Admin/exams/records") ||
                    p.includes("/students/report-card") ||
                    p.includes("/students/transcript"),
            },
            {
                to: "/Admin/exams/ca",
                label: "Continuous Assessment",
                hint: "Class tests, assignments, portfolios & rubrics (40% of term mark)",
                match: (p) => p.startsWith("/Admin/exams/ca"),
            },
            {
                to: "/Admin/maneb",
                label: "MANEB examinations",
                hint: "PSLCE · JCE · MSCE candidates, mocks & certificates",
                match: (p) => p.startsWith("/Admin/maneb"),
            },
            {
                to: "/Admin/classes/results/overview",
                label: "Results overview",
                hint: "Live summary (includes drafts)",
                match: (p) => p.includes("/Admin/classes/results"),
            },
            {
                to: "/Admin/curriculum",
                label: "Curriculum guide",
                hint: "What each level should cover",
                match: (p) => p.startsWith("/Admin/curriculum"),
            },
        ],
    },
    {
        id: "fees",
        stepLabel: "Step 5",
        title: "School fees",
        summary: "Setup → collect → follow up",
        items: [
            {
                to: "/Admin/fees/structures",
                label: "1. Fee setup",
                hint: "Set fees per class (do once per term)",
                match: (p) => p.startsWith("/Admin/fees/structures"),
            },
            {
                to: "/Admin/fees/payment-settings",
                label: "Student payment details",
                hint: "Bank / mobile money on student portal",
                match: (p) => p.startsWith("/Admin/fees/payment-settings"),
            },
            {
                to: "/Admin/fees/collection",
                label: "2. Collect fees",
                hint: "Cash book — record payments",
                match: (p) => p.startsWith("/Admin/fees/collection") || p.startsWith("/Admin/fees/record"),
            },
            {
                to: "/Admin/fees/payment-submissions",
                label: "Verify student payments",
                hint: "Confirm cash-in, transfer & balance — proof may be invalid",
                match: (p) => p.startsWith("/Admin/fees/payment-submissions"),
            },
            {
                to: "/Admin/fees/defaulters",
                label: "3. Who owes fees",
                hint: "Defaulters list",
                match: (p) => p.startsWith("/Admin/fees/defaulters"),
            },
            {
                to: "/Admin/fees/aids",
                label: "Waivers & bursaries",
                hint: "Scholarships, subsidies & government bursaries",
                match: (p) => p.startsWith("/Admin/fees/aids"),
            },
            {
                to: "/Admin/fees/reports",
                label: "4. Fee reports",
                hint: "Summary for bursar / head",
                match: (p) => p.startsWith("/Admin/fees/reports"),
            },
        ],
    },
    {
        id: "expenses",
        stepLabel: "Step 6",
        title: "School expenses",
        summary: "Bills, budgets & reports",
        items: [
            {
                to: "/Admin/expenses/log",
                label: "Expense book",
                hint: "Record money spent",
                match: (p) => p.startsWith("/Admin/expenses/log") || p.includes("/Admin/expenses/detail"),
            },
            {
                to: "/Admin/expenses/budgets",
                label: "Term budgets",
                hint: "Per-term spending plans & teacher material requests",
                match: (p) => p.startsWith("/Admin/expenses/budgets"),
            },
            {
                to: "/Admin/expenses/summary",
                label: "Expense reports",
                hint: "Summaries for management",
                match: (p) => p.startsWith("/Admin/expenses/summary"),
            },
        ],
    },
    {
        id: "planning",
        stepLabel: "Extra",
        title: "Timetable & calendar",
        summary: "Planning the term",
        items: [
            {
                to: "/Admin/timetables",
                label: "Class timetables",
                hint: "Who teaches when",
                match: (p) => p.startsWith("/Admin/timetables"),
            },
            {
                to: "/Admin/academic-calendar",
                label: "Academic calendar",
                hint: "Term dates & deadlines (all staff view)",
                match: (p) => p.startsWith("/Admin/academic-calendar"),
            },
            {
                to: "/Admin/academic-years",
                label: "Academic years & terms",
                hint: "Create years, open/close terms",
                match: (p) => p.startsWith("/Admin/academic-years"),
            },
            {
                to: "/Admin/calendar",
                label: "Full school calendar",
                hint: "Exams, events & timetables",
                match: (p) => p.startsWith("/Admin/calendar") && !p.startsWith("/Admin/academic"),
            },
        ],
    },
    {
        id: "library",
        stepLabel: "Resources",
        title: "Library & TLMs",
        summary: "Books, materials & borrowing",
        items: [
            {
                to: "/Admin/library/overview",
                label: "Term resources overview",
                hint: "Digital uploads, downloads & term loans",
                match: (p) => p.startsWith("/Admin/library/overview"),
            },
            {
                to: "/Admin/library",
                label: "Library inventory",
                hint: "Books, TLMs, loans & stock",
                match: (p) => p.startsWith("/Admin/library") && !p.startsWith("/Admin/library/overview"),
            },
        ],
    },
    {
        id: "infrastructure",
        stepLabel: "Facilities",
        title: "Infrastructure & assets",
        summary: "Buildings, equipment & maintenance",
        items: [
            {
                to: "/Admin/infrastructure",
                label: "Facilities & assets",
                hint: "Rooms, furniture, maintenance log",
                match: (p) => p.startsWith("/Admin/infrastructure"),
            },
        ],
    },
];

export const ADMIN_HOSTEL_SECTION = {
    id: "hostels",
    stepLabel: "Boarding",
    title: "Hostels & boarding",
    summary: "Rooms and boarders",
    items: [
        {
            to: "/Admin/hostels",
            label: "Hostels & rooms",
            hint: "Dormitory register",
            match: (p) => p.startsWith("/Admin/hostels"),
        },
        {
            to: "/Admin/hostels/boarders",
            label: "Boarder lists",
            hint: "Who sleeps where",
            match: (p) => p.startsWith("/Admin/hostels/boarders"),
        },
    ],
};

/** In-page guide copy keyed by route prefix (longest match wins). */
export const ADMIN_PAGE_GUIDES = [
    {
        match: (p) => p === "/Admin/dashboard" || p === "/Admin",
        title: "School office — start here",
        paper: "Daily diary + summary counts",
        steps: [
            "Follow Step 1 → Step 6 in the menu (same order you would set up paper files).",
            "Green numbers show live totals from your school.",
            "Use the checklist below if you are setting up for the first time.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/addclass") || p.startsWith("/Admin/classes"),
        title: "Class registers & form teachers",
        paper: "Class register books (Std 1, Form 2, etc.)",
        steps: [
            "Add each class your school runs (e.g. Std 5, Form 3).",
            "Assign a form teacher on each class — they enroll pupils and prepare class results.",
            "Open a class to manage subjects and the student list.",
        ],
        tip: "If a class already exists, you will see a clear message — no need to add it twice.",
    },
    {
        match: (p) => p.startsWith("/Admin/enrollment/review"),
        title: "Verify form-teacher enrollments",
        paper: "Admission register pending approval",
        steps: [
            "Review pupils enrolled by form teachers before fees are created.",
            "Approve or reject each student; check roll declarations match your records (anti–fee theft).",
            "After approval, fees apply automatically if fee structures exist.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/subjects") || p.startsWith("/Admin/addsubject"),
        title: "Subjects per class",
        paper: "Subject list for each class",
        steps: [
            "Choose the class first (each class has its own subject list).",
            "Add subjects (Math, English, etc.) and set periods per week.",
            "Assign a teacher to each subject where possible.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/teachers/management"),
        title: "Teacher HR & TSC",
        paper: "Teachers Service Commission register",
        steps: [
            "Review TSC verification status and pending qualification documents.",
            "Open a teacher profile → HR dossier for CPD, deployment history, appraisals, and payslips.",
            "Approve leave applications from the leave file when teachers apply.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/teachers") || p.startsWith("/Admin/staff"),
        title: "School staff",
        paper: "Staff register",
        steps: [
            "Add staff (teacher, bursar, head of department, librarian) with temporary login credentials.",
            "They complete their profile after first sign-in. Assign subjects to teachers and HOD from each profile.",
            "When you appoint a bursar, they record day-to-day fees and expenses in their portal; you still see the same live data here.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/students/chooseclass") || p.startsWith("/Admin/addstudents"),
        title: "Enrol a new student",
        paper: "Admission / enrolment form",
        steps: [
            "Pick the class first (like writing them in the class register).",
            "Fill name, guardian details, and photo if you have one.",
            "Save — you will see a success message when the pupil is added.",
        ],
        tip: "Fees for the class are applied automatically if you set up fee structures (Step 5).",
    },
    {
        match: (p) => p.startsWith("/Admin/students"),
        title: "Student records",
        paper: "Master student list",
        steps: [
            "Search or open a class to see the class list.",
            "Click a student for attendance, marks, and report card.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/addnotice"),
        title: "Post a notice",
        paper: "School notice board",
        steps: [
            "Write a short title and message for parents and students.",
            "Choose who should see it (all, teachers only, etc.).",
            "Save — it appears on the notice list immediately.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/attendance"),
        title: "Attendance",
        paper: "Attendance registers",
        steps: [
            "Teachers mark attendance in their portal.",
            "You review and approve submissions here.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/exams/review"),
        title: "Approve class results (form teacher packages)",
        paper: "Signed class results bundle",
        steps: [
            "Subject teachers submit marks to their form teacher.",
            "Form teacher reviews all subjects and sends one package to you.",
            "Approve here to publish report cards on the student portal.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/exams"),
        title: "Exam marks & results",
        paper: "Marks book / exam results",
        steps: [
            "Use Approve class results for form-teacher packages.",
            "Use Results overview for class summaries and printable cards.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/fees/structures"),
        title: "Fee setup (do this before collecting)",
        paper: "Fee tariff sheet per class",
        steps: [
            "Set how much each class pays per term.",
            "New students in that class are billed automatically.",
        ],
        tip: "If every class already has fees for this term, the system will tell you — edit existing instead of duplicating.",
    },
    {
        match: (p) => p.startsWith("/Admin/fees/collection") || p.startsWith("/Admin/fees/record"),
        title: "Collect school fees",
        paper: "Fees cash book",
        steps: [
            "Find the student, record how much was paid and the date.",
            "Print or share receipt details with the guardian if needed.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/fees/defaulters"),
        title: "Fee defaulters",
        paper: "List of balances owing",
        steps: [
            "Shows students who still owe money.",
            "Follow up by phone or notice, then record payment in Collect fees.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/fees"),
        title: "School fees",
        paper: "Fees office records",
        steps: ["Work in order: Setup → Collect → Defaulters → Reports."],
    },
    {
        match: (p) => p.startsWith("/Admin/expenses"),
        title: "School expenses",
        paper: "Petty cash / expense ledger",
        steps: [
            "Record each bill or payment when money is spent.",
            "Use Budgets to plan; use Reports to summarise.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/infrastructure"),
        title: "Infrastructure & assets",
        paper: "Facilities register + asset inventory + maintenance log",
        steps: [
            "Register classrooms, labs, dormitories, and other spaces under Facilities.",
            "Add furniture, computers, and equipment under Assets — link each to a facility where possible.",
            "Log maintenance requests; mark complete to update asset condition and facility status.",
        ],
        tip: "Use Reports to see assets in poor/broken condition and open work orders at a glance.",
    },
    {
        match: (p) => p.startsWith("/Admin/library"),
        title: "Library & TLMs inventory",
        paper: "Library register + borrowing ledger",
        steps: [
            "Add books and teaching materials with copy counts and shelf locations.",
            "Record borrowing for students or teachers with a due date.",
            "Use Reports for low-stock alerts and category summaries.",
        ],
        tip: "Set a low-stock threshold per item to get warnings when copies run out.",
    },
    {
        match: (p) => p.startsWith("/Admin/school-page"),
        title: "Parents’ school page",
        paper: "School brochure for the public",
        steps: [
            "Edit text and photos, set your school web address (slug).",
            "Press Publish when ready — parents can open the link without logging in.",
        ],
    },
    {
        match: (p) => p.startsWith("/Admin/complains"),
        title: "Complaints",
        paper: "Complaints register",
        steps: ["Read complaints from students; respond or mark as handled."],
    },
    {
        match: (p) => p.startsWith("/Admin/profile"),
        title: "Your profile",
        paper: "Head teacher / admin details",
        steps: ["Update your name, school name, or password here."],
    },
];

export function getPageGuide(pathname, schoolType) {
    const guides = schoolType != null ? getAdminPageGuides(schoolType) : ADMIN_PAGE_GUIDES;
    for (const g of guides) {
        if (g.match(pathname)) return g;
    }
    return null;
}

/** True when every class has a homeroom / form teacher assigned. */
export function allClassesHaveHomeroom(classes) {
    const list = Array.isArray(classes) ? classes : [];
    if (!list.length) return false;
    return list.every((c) => {
        const teacherId =
            c?.classTeacherId ?? c?.classTeacher?.id ?? c?.classTeacher?._id;
        return Boolean(String(teacherId || "").trim());
    });
}

export function getSetupChecklist({
    classCount = 0,
    teacherCount = 0,
    studentCount = 0,
    feeStructureCount = 0,
    primaryTermConfigured = false,
    secondaryTermConfigured = false,
    schoolType,
    offersBoarding = false,
    hostelWithRooms = false,
    hasPrimaryClasses = true,
    hasSecondaryClasses = true,
    allHomeroomsAssigned = false,
}) {
    const term = getHomeroomTeacherTerm(schoolType);
    const homeroom = term.assignOnClass;

    const termSetupItems = [];
    if (schoolType === "Combined") {
        if (hasPrimaryClasses) {
            termSetupItems.push(
                {
                    key: "term-primary",
                    done: primaryTermConfigured,
                    label: "Primary: set Term 1–3 open and closing dates",
                    to: "/Admin/academic-years",
                },
            );
        }
        if (hasSecondaryClasses) {
            termSetupItems.push(
                {
                    key: "term-secondary",
                    done: secondaryTermConfigured,
                    label: "Secondary: set Term 1–3 open and closing dates",
                    to: "/Admin/academic-years",
                },
            );
        }
    } else if (schoolType === "Primary") {
        termSetupItems.push({
            key: "term-primary",
            done: primaryTermConfigured,
            label: "Set Term 1–3 open and closing dates (Primary)",
            to: "/Admin/academic-years",
        });
    } else if (schoolType === "Secondary") {
        termSetupItems.push({
            key: "term-secondary",
            done: secondaryTermConfigured,
            label: "Set Term 1–3 open and closing dates (Secondary)",
            to: "/Admin/academic-years",
        });
    }

    return [
        {
            key: "classes",
            done: classCount > 0,
            label: "Add at least one class",
            to: "/Admin/addclass",
        },
        ...termSetupItems,
        {
            key: "staff",
            done: teacherCount > 0,
            label: "Add at least one staff member",
            to: "/Admin/staff/role",
        },
        {
            key: "homeroom",
            done: allHomeroomsAssigned,
            label: `Assign a ${homeroom} on each class`,
            to: "/Admin/classes",
        },
        ...(offersBoarding
            ? [
                  {
                      key: "hostels",
                      done: hostelWithRooms,
                      label: "Add at least one boys or girls hostel with rooms",
                      to: "/Admin/hostels",
                  },
              ]
            : []),
        {
            key: "students",
            done: studentCount > 0,
            label: `Enrol at least one student (or via ${homeroom})`,
            to: "/Admin/students/chooseclass",
        },
        {
            key: "fees",
            done: feeStructureCount > 0,
            label: "Set up fees for this term",
            to: "/Admin/fees/structures",
        },
    ];
}
